import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric, Skeleton, Tooltip } from '@blade/ui-kit'
import { nativePriceByNetwork } from '@root/features/realtime/atoms'
import { useTranslation } from '@root/shared/localization'
import { Avatar, InfoMessage, Truncate } from '@root/shared/ui-kit'

import { reatomWalletTransfers, walletProfileModalAtom } from '../atoms'
import { numeraireOf, valueInUsd } from '../model'

import { WalletProfileAgo } from './wallet-profile-ago'

import type { WalletTransfers } from '../model'
import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'

/**
 * The money a wallet has moved.
 *
 * Laid out as the positions table beside it — a heading row, an icon and a
 * ticker for the asset, money to the right — because a reader moving between
 * tabs should not have to learn a second table.
 *
 * Two things the figures cannot say are said above them instead of being left
 * for a reader to work out: the history only goes back as far as it is kept,
 * so a short list is not a quiet wallet, and only SOL and stablecoins are
 * indexed, so a token moving between two wallets is not here at all.
 *
 * Saying that once, plainly, is the difference between a list that is
 * incomplete and a list that lies.
 */

type Props = {
  walletAddress: Address
  network: Network.Network
}

export const WalletProfileTransfers: React.FC<Props> = ({
  walletAddress,
  network,
}) => {
  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomWalletTransfers({ walletAddress, network }),
    [walletAddress, network],
  )
  const [state] = useAtom(model)
  const refresh = useAction(model.refresh)

  return (
    <Root>
      {AsyncStateN.matchWaiting(state, {
        Error: () => (
          <InfoMessage intent="error" variant="dt" onRetry={refresh} />
        ),
        Waiting: () => (
          <Rows>
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} width="100%" height={22} />
            ))}
          </Rows>
        ),
        Success: (moved) => <Moved moved={moved} network={network} />,
      })}
    </Root>
  )
}

const Moved: React.FC<{
  moved: WalletTransfers.WalletTransfers
  network: Network.Network
}> = ({ moved, network }) => {
  const { t } = useTranslation()
  // Today's price for the native coin, so a SOL amount carries a dollar
  // figure. Zero while the socket has not spoken yet, which the conversion
  // reads as "cannot say" rather than as "worth nothing".
  const [nativePrice] = useAtom(nativePriceByNetwork[network])
  // Read once for the whole list: the same moment for every row.
  const now = useMemo(() => new Date(), [moved])

  const openProfile = useAction(
    (ctx, walletAddress: Address) =>
      walletProfileModalAtom(ctx, { walletAddress, network }),
    [network],
  )

  if (moved.transfers.length === 0) {
    return (
      <>
        <Note>
          {t('wallet-profile.transfers.note', { days: moved.retainedDays })}
        </Note>
        <Empty>
          {t('wallet-profile.transfers.empty', { days: moved.retainedDays })}
        </Empty>
      </>
    )
  }

  return (
    <>
      <Note>
        {t('wallet-profile.transfers.note', { days: moved.retainedDays })}
      </Note>
      <Rows>
        <Head>
          <span>{t('wallet-profile.column.time')}</span>
          <span>{t('wallet-profile.column.action')}</span>
          <span>{t('wallet-profile.column.counterparty')}</span>
          <span>{t('wallet-profile.column.asset')}</span>
          <Right>{t('wallet-profile.column.amount')}</Right>
          <Right>
            <Tooltip content={t('wallet-profile.column.value-note')}>
              <Hint>{t('wallet-profile.column.value')}</Hint>
            </Tooltip>
          </Right>
        </Head>
        {moved.transfers.map((transfer) => {
          const asset = numeraireOf(transfer.mint)
          const worth = valueInUsd(
            transfer.mint,
            transfer.amount,
            nativePrice.toNumber(),
          )

          return (
            <Row key={`${transfer.signature}-${transfer.direction}`}>
              <WalletProfileAgo at={transfer.at} now={now} />
              <Direction data-direction={transfer.direction}>
                {t(
                  transfer.direction === 'in'
                    ? 'wallet-profile.transfers.in'
                    : 'wallet-profile.transfers.out',
                )}
              </Direction>
              {/* The other wallet is a wallet we have a profile for, so it
                  opens one rather than being eight characters to copy out. */}
              <Counterparty
                type="button"
                onClick={() => openProfile(transfer.counterparty as Address)}
              >
                <Truncate start={4} end={4}>
                  {transfer.counterparty}
                </Truncate>
              </Counterparty>
              <Asset>
                <Avatar size={16} src={asset.logo} alt={asset.ticker} />
                <AssetTicker>{asset.ticker}</AssetTicker>
              </Asset>
              <Right data-direction={transfer.direction}>
                <Sign>{transfer.direction === 'in' ? '+' : '−'}</Sign>
                <Numeric
                  value={transfer.amount}
                  preset="currency-amount-short"
                />
              </Right>
              <Right>
                {worth === undefined ? (
                  <Unknown>—</Unknown>
                ) : (
                  <Numeric
                    value={worth}
                    preset="currency-amount-short"
                    currency="$"
                  />
                )}
              </Right>
            </Row>
          )
        })}
      </Rows>
    </>
  )
}

const COLUMNS = `48px 52px minmax(72px, 1fr) minmax(56px, 76px) minmax(72px, 96px) minmax(64px, 88px)`
const NARROW_COLUMNS = `44px 48px minmax(64px, 1fr) minmax(56px, 76px) minmax(68px, 88px)`

const Root = styled.div`
  overflow: auto;
  display: grid;
  align-content: start;
  gap: 8px;
`

const Note = styled.p`
  margin: 0;
  padding: 0 6px 6px;
  border-bottom: 1px solid var(--background-3);

  color: var(--text-9);
  font-size: 10px;
  line-height: 14px;
`

const Rows = styled.div`
  display: grid;
  gap: 2px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 12px;
  align-items: center;

  padding: 5px 6px;
  border-radius: 6px;

  color: var(--text-10);
  font-size: 11px;
  font-variant-numeric: tabular-nums;

  &:hover {
    background: var(--appearanceA-1);
  }

  /* A phone keeps when, which way, and how much; the dollar figure and the
     asset column are what a narrow screen can afford to lose, in that
     order. */
  @media ${({ theme }) => theme.tablet.queryMaxWidth} {
    grid-template-columns: ${NARROW_COLUMNS};
    gap: 8px;

    & > :last-child {
      display: none;
    }
  }
`

/** Stays put while the rows scroll; a table without headings is a wall. */
const Head = styled(Row)`
  position: sticky;
  top: 0;
  z-index: 1;

  background: var(--background-1);
  color: var(--text-9);
  font-size: 10px;

  &:hover {
    background: var(--background-1);
  }
`

const Direction = styled.span`
  font-size: 10px;
  font-weight: 500;

  &[data-direction='in'] {
    color: var(--up-9);
  }

  &[data-direction='out'] {
    color: var(--down-9);
  }
`

const Counterparty = styled.button`
  overflow: hidden;
  padding: 0;
  border: none;
  background: none;

  color: var(--text-10);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: var(--text-12);
    text-decoration: underline;
  }
`

const Asset = styled.div`
  overflow: hidden;
  display: flex;
  gap: 6px;
  align-items: center;
`

const AssetTicker = styled.span`
  overflow: hidden;

  color: var(--text-11);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Right = styled.div`
  text-align: right;

  &[data-direction='in'] {
    color: var(--up-9);
  }

  &[data-direction='out'] {
    color: var(--down-9);
  }
`

/** Carries the direction on its own, for a reader who does not see colour. */
const Sign = styled.span`
  margin-right: 1px;
`

const Hint = styled.span`
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: help;
`

const Unknown = styled.span`
  color: var(--text-9);
`

const Empty = styled.div`
  display: grid;
  place-items: center;

  padding-block: 24px;

  color: var(--text-9);
  font-size: 11px;
`
